// src/components/admin/NavbarConfigEditor.jsx
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { database } from "../../firebaseConfig";
import { ref, get, set } from "firebase/database";
import "./NavbarConfigEditor.css";

import { config } from '../../config/config'
const API_URL = config.baseUrl;

// Default Navbar configuration structure with gradient colors, logo, and phone logo.
const defaultNavbarConfig = {
    direction: "to right", // e.g., "to right", "45deg"
    colors: ["#000000", "#ffffff"], // array of gradient colors
    logoImageUrl: "",
    showLogo: true,
    // New field for phone view logo
    phoneLogoImageUrl: "",
};

const NavbarConfigEditor = () => {
    const [navbarConfig, setNavbarConfig] = useState(defaultNavbarConfig);
    const [loading, setLoading] = useState(true);
    const logoInputRef = useRef(null);
    const phoneLogoInputRef = useRef(null);

    useEffect(() => {
        const fetchNavbarConfig = async () => {
            try {
                const snapshot = await get(ref(database, "navbarConfig"));
                if (snapshot.exists()) {
                    // Merge fetched config with defaults.
                    setNavbarConfig({ ...defaultNavbarConfig, ...snapshot.val() });
                }
            } catch (error) {
                console.error("Error fetching Navbar config:", error);
            }
            setLoading(false);
        };
        fetchNavbarConfig();
    }, []);

    // Helper: Upload file to server.
    const uploadFile = async (file) => {
        if (!file) return "";
        const formData = new FormData();
        formData.append("media", file);
        try {
            const response = await axios.post(`${API_URL}upload`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return response.data.fileUrl || "";
        } catch (error) {
            console.error("Error uploading file:", error);
            return "";
        }
    };

    // Handle logo image change.
    const handleLogoChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            const fileUrl = await uploadFile(file);
            setNavbarConfig((prev) => ({ ...prev, logoImageUrl: fileUrl }));
        }
    };

    // Handle phone logo image change.
    const handlePhoneLogoChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            const fileUrl = await uploadFile(file);
            setNavbarConfig((prev) => ({ ...prev, phoneLogoImageUrl: fileUrl }));
        }
    };

    // Delete function to remove logo image.
    const deleteFile = async (fileUrl) => {
        if (!fileUrl) return;
        const parts = fileUrl.split("/");
        const filename = parts[parts.length - 1];
        try {
            await axios.delete(`${API_URL}delete`, { data: { filename } });
            console.log("Deleted file:", filename);
        } catch (error) {
            console.error("Error deleting file:", error);
        }
    };

    const handleDeleteLogo = () => {
        deleteFile(navbarConfig.logoImageUrl);
        setNavbarConfig((prev) => ({ ...prev, logoImageUrl: "" }));
    };

    const handleDeletePhoneLogo = () => {
        deleteFile(navbarConfig.phoneLogoImageUrl);
        setNavbarConfig((prev) => ({ ...prev, phoneLogoImageUrl: "" }));
    };

    // Handle changing a specific gradient color.
    const handleColorChange = (index, value) => {
        const newColors = [...navbarConfig.colors];
        newColors[index] = value;
        setNavbarConfig((prev) => ({ ...prev, colors: newColors }));
    };

    // Add a new color to the gradient.
    const addColor = () => {
        setNavbarConfig((prev) => ({ ...prev, colors: [...prev.colors, "#000000"] }));
    };

    // Remove a color at a specific index.
    const removeColor = (index) => {
        const newColors = navbarConfig.colors.filter((_, i) => i !== index);
        setNavbarConfig((prev) => ({ ...prev, colors: newColors }));
    };

    // Build the linear-gradient CSS string.
    const gradientString = `linear-gradient(${navbarConfig.direction}, ${navbarConfig.colors.join(", ")})`;

    // Save configuration to Firebase.
    const handleSaveConfig = async () => {
        console.log("Saving navbar config:", navbarConfig);
        try {
            await set(ref(database, "navbarConfig"), navbarConfig);
            alert("Navbar configuration saved successfully!");
        } catch (error) {
            console.error("Error saving Navbar config:", error);
            alert("Error saving configuration.");
        }
    };

    if (loading) {
        return <div className="NavbarConfigEditor-loading">Loading Navbar configuration...</div>;
    }

    return (
        <div className="NavbarConfigEditor">
            <h2>Edit Navbar Configuration</h2>
            <div className="NavbarConfigEditor-form-group">
                <label>Navbar Background (CSS Linear Gradient):</label>
                <div className="NavbarConfigEditor-gradient-config">
                    <label>Direction:</label>
                    <input
                        type="text"
                        value={navbarConfig.direction}
                        onChange={(e) =>
                            setNavbarConfig((prev) => ({ ...prev, direction: e.target.value }))
                        }
                        placeholder="e.g., to right, 45deg"
                    />
                    <div className="NavbarConfigEditor-colors-list">
                        {navbarConfig.colors.map((color, index) => (
                            <div key={index} className="NavbarConfigEditor-color-item">
                                <input
                                    type="color"
                                    value={color}
                                    onChange={(e) => handleColorChange(index, e.target.value)}
                                />
                                <button className="NavbarConfigEditor-remove-color" onClick={() => removeColor(index)}>
                                    Remove
                                </button>
                            </div>
                        ))}
                    </div>
                    <button className="NavbarConfigEditor-add-color" onClick={addColor}>
                        Add Color
                    </button>
                </div>
                <div className="NavbarConfigEditor-gradient-preview">
                    <label>Preview:</label>
                    <div className="NavbarConfigEditor-preview-box" style={{ background: gradientString }}></div>
                </div>
            </div>

            <div className="NavbarConfigEditor-form-group">
                <label>Show Logo:</label>
                <input
                    type="checkbox"
                    checked={navbarConfig.showLogo}
                    onChange={(e) =>
                        setNavbarConfig((prev) => ({ ...prev, showLogo: e.target.checked }))
                    }
                />
            </div>

            <div className="NavbarConfigEditor-form-group">
                <label>Logo Image:</label>
                {navbarConfig.logoImageUrl && (
                    <div className="NavbarConfigEditor-logo-preview">
                        <img src={navbarConfig.logoImageUrl} alt="Logo Preview" />
                        <button onClick={handleDeleteLogo}>Delete Logo</button>
                    </div>
                )}
                <input
                    type="file"
                    accept="image/*"
                    ref={logoInputRef}
                    onChange={handleLogoChange}
                />
            </div>

            <div className="NavbarConfigEditor-form-group">
                <label>Phone Logo Image (for mobile view):</label>
                {navbarConfig.phoneLogoImageUrl && (
                    <div className="NavbarConfigEditor-logo-preview">
                        <img src={navbarConfig.phoneLogoImageUrl} alt="Phone Logo Preview" />
                        <button onClick={handleDeletePhoneLogo}>Delete Phone Logo</button>
                    </div>
                )}
                <input
                    type="file"
                    accept="image/*"
                    ref={phoneLogoInputRef}
                    onChange={handlePhoneLogoChange}
                />
            </div>

            <button onClick={handleSaveConfig} className="NavbarConfigEditor-save-button">
                Save Navbar Configuration
            </button>
        </div>
    );
};

export default NavbarConfigEditor;
